import React from 'react';
import { ArrowRight, Ruler, Zap, FileText, Users, CheckCircle, Clock } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '../context/TranslationContext';
import LanguageSwitcher from '../components/LanguageSwitcher';

const LandingPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="border-b border-gray-200 bg-white sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-2">
              <img
                src="/images/Logo.png"
                alt="Bestonlinekozijnen"
                className="h-10 w-auto object-contain"
              />
            </div>
            <div className="hidden md:flex items-center space-x-8">
              <a href="#features" className="text-gray-600 hover:text-gray-900 transition-colors">{t('landing.navFeatures')}</a>
              <a href="#how-it-works" className="text-gray-600 hover:text-gray-900 transition-colors">{t('landing.navHowItWorks')}</a>
              <a href="/pricing" onClick={(e) => { e.preventDefault(); navigate('/pricing'); }} className="text-gray-600 hover:text-gray-900 transition-colors">{t('landing.navPricing')}</a>
              <Button onClick={() => navigate('/configurator-select')} className="bg-orange-600 hover:bg-orange-700">
                {t('landing.navTryConfigurator')}
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
              <LanguageSwitcher />
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-slate-50 to-slate-100 py-20 md:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <Badge className="mb-4 bg-orange-100 text-orange-800 border-orange-200">
                {t('landing.badgeForCompanies')}
              </Badge>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
                {t('landing.heroTitle')}
                <span className="text-orange-600"> {t('landing.heroTitleHighlight')}</span>
              </h1>
              <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                {t('landing.heroDescription')}
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button 
                  size="lg" 
                  onClick={() => navigate('/configurator-select')}
                  className="bg-orange-600 hover:bg-orange-700 text-white text-lg px-8 py-6"
                >
                  {t('landing.ctaStartConfiguring')}
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
                <Button 
                  size="lg" 
                  variant="outline"
                  className="border-2 border-gray-300 text-lg px-8 py-6"
                >
                  {t('landing.ctaWatchDemo')}
                </Button>
              </div>
              <div className="mt-8 flex items-center space-x-6 text-sm text-gray-600">
                <div className="flex items-center">
                  <CheckCircle className="w-5 h-5 text-green-600 mr-2" />
                  {t('landing.noCreditCard')}
                </div>
                <div className="flex items-center">
                  <CheckCircle className="w-5 h-5 text-green-600 mr-2" />
                  {t('landing.freeTrial')}
                </div>
              </div>
            </div>
            <div className="relative">
              <div className="bg-white rounded-2xl shadow-2xl p-6 border-4 border-orange-500">
                <img 
                  src="/images/config.png" 
                  alt="Window Configuration"
                  className="rounded-lg w-full"
                />
                <div className="absolute -bottom-6 -left-6 bg-white rounded-xl shadow-xl p-4 border border-gray-200">
                  <div className="flex items-center space-x-3">
                    <div className="bg-green-100 p-2 rounded-lg">
                      <Clock className="w-6 h-6 text-green-600" />
                    </div>
                    <div>
                      <div className="text-sm text-gray-600">Average Time</div>
                      <div className="text-xl font-bold text-gray-900">5 min</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-12 bg-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            <div>
              <div className="text-4xl font-bold text-orange-500">95%</div>
              <div className="text-slate-300 mt-2">{t('landing.statsAccuracy')}</div>
            </div>
            <div>
              <div className="text-4xl font-bold text-orange-500">3 Min</div>
              <div className="text-slate-300 mt-2">{t('landing.statsAvgConfig')}</div>
            </div>
            <div>
              <div className="text-4xl font-bold text-orange-500">500+</div>
              <div className="text-slate-300 mt-2">{t('landing.statsActiveCompanies')}</div>
            </div>
            <div>
              <div className="text-4xl font-bold text-orange-500">50K+</div>
              <div className="text-slate-300 mt-2">{t('landing.statsQuotesGenerated')}</div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <Badge className="mb-4 bg-orange-100 text-orange-800 border-orange-200">
              Powerful Features
            </Badge>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              {t('landing.featuresTitle')}
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {t('landing.featuresDescription')}
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <Card className="border-2 hover:border-orange-500 transition-all hover:shadow-xl">
              <CardHeader>
                <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mb-4">
                  <Ruler className="w-6 h-6 text-orange-600" />
                </div>
                <CardTitle>{t('landing.featurePreciseMeasurements')}</CardTitle>
                <CardDescription>
                  {t('landing.featurePreciseMeasurementsDesc')}
                </CardDescription>
              </CardHeader>
            </Card>

            <Card className="border-2 hover:border-orange-500 transition-all hover:shadow-xl">
              <CardHeader>
                <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mb-4">
                  <Zap className="w-6 h-6 text-orange-600" />
                </div>
                <CardTitle>{t('landing.featureInstantQuotes')}</CardTitle>
                <CardDescription>
                  {t('landing.featureInstantQuotesDesc')}
                </CardDescription>
              </CardHeader>
            </Card>

            <Card className="border-2 hover:border-orange-500 transition-all hover:shadow-xl">
              <CardHeader>
                <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mb-4">
                  <FileText className="w-6 h-6 text-orange-600" />
                </div>
                <CardTitle>{t('landing.featureEngineerReady')}</CardTitle>
                <CardDescription>
                  {t('landing.featureEngineerReadyDesc')}
                </CardDescription>
              </CardHeader>
            </Card>

            <Card className="border-2 hover:border-orange-500 transition-all hover:shadow-xl">
              <CardHeader>
                <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mb-4">
                  <Users className="w-6 h-6 text-orange-600" />
                </div>
                <CardTitle>{t('landing.featureMultiUser')}</CardTitle>
                <CardDescription>
                  {t('landing.featureMultiUserDesc')}
                </CardDescription>
              </CardHeader>
            </Card>

            <Card className="border-2 hover:border-orange-500 transition-all hover:shadow-xl">
              <CardHeader>
                <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mb-4">
                  <CheckCircle className="w-6 h-6 text-orange-600" />
                </div>
                <CardTitle>{t('landing.featurePremiumSystems')}</CardTitle>
                <CardDescription>
                  {t('landing.featurePremiumSystemsDesc')}
                </CardDescription>
              </CardHeader>
            </Card>

            <Card className="border-2 hover:border-orange-500 transition-all hover:shadow-xl">
              <CardHeader>
                <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mb-4">
                  <Clock className="w-6 h-6 text-orange-600" />
                </div>
                <CardTitle>{t('landing.featureMobileOptimized')}</CardTitle>
                <CardDescription>
                  {t('landing.featureMobileOptimizedDesc')}
                </CardDescription>
              </CardHeader>
            </Card>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section id="how-it-works" className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <Badge className="mb-4 bg-orange-100 text-orange-800 border-orange-200">
              Simple Process
            </Badge>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              {t('landing.howItWorksTitle')}
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="relative">
              <div className="bg-white rounded-xl p-8 shadow-lg border-2 border-gray-200 hover:border-orange-500 transition-all">
                <div className="w-16 h-16 bg-orange-600 text-white rounded-full flex items-center justify-center text-2xl font-bold mb-6">
                  1
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{t('landing.step1Title')}</h3>
                <p className="text-gray-600">
                  {t('landing.step1Desc')}
                </p>
              </div>
            </div>

            <div className="relative">
              <div className="bg-white rounded-xl p-8 shadow-lg border-2 border-gray-200 hover:border-orange-500 transition-all">
                <div className="w-16 h-16 bg-orange-600 text-white rounded-full flex items-center justify-center text-2xl font-bold mb-6">
                  2
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{t('landing.step2Title')}</h3>
                <p className="text-gray-600">
                  {t('landing.step2Desc')}
                </p>
              </div>
            </div>

            <div className="relative">
              <div className="bg-white rounded-xl p-8 shadow-lg border-2 border-gray-200 hover:border-orange-500 transition-all">
                <div className="w-16 h-16 bg-orange-600 text-white rounded-full flex items-center justify-center text-2xl font-bold mb-6">
                  3
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{t('landing.step3Title')}</h3>
                <p className="text-gray-600">
                  {t('landing.step3Desc')}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-orange-600 to-orange-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            {t('landing.ctaSectionTitle')}
          </h2>
          <p className="text-xl text-orange-100 mb-8 max-w-2xl mx-auto">
            {t('landing.ctaSectionDescription')}
          </p>
          <Button 
            size="lg"
            onClick={() => navigate('/configurator-select')}
            className="bg-white text-orange-600 hover:bg-slate-100 text-lg px-8 py-6"
          >
            {t('landing.ctaStartFreeTrial')}
            <ArrowRight className="ml-2 w-5 h-5" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-slate-300 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center space-x-2 mb-4">
                <Ruler className="w-8 h-8 text-orange-600" />
                <span className="text-xl font-bold text-white">Bestonline<span className="text-orange-600">kozijnen</span></span>
              </div>
              <p className="text-slate-400">
                {t('landing.footerDescription')}
              </p>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4">{t('landing.footerProduct')}</h4>
              <ul className="space-y-2">
                <li><a href="#features" className="hover:text-orange-500 transition-colors">{t('landing.navFeatures')}</a></li>
                <li><a href="/pricing" onClick={(e) => { e.preventDefault(); navigate('/pricing'); }} className="hover:text-orange-500 transition-colors">{t('landing.navPricing')}</a></li>
                <li><a href="/configurator-select" onClick={(e) => { e.preventDefault(); navigate('/configurator-select'); }} className="hover:text-orange-500 transition-colors">{t('landing.navTryConfigurator')}</a></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4">{t('landing.footerCompany')}</h4>
              <ul className="space-y-2">
                <li><a href="#" className="hover:text-orange-500 transition-colors">{t('landing.footerAboutUs')}</a></li>
                <li><a href="#" className="hover:text-orange-500 transition-colors">{t('landing.footerContact')}</a></li>
                <li><a href="#" className="hover:text-orange-500 transition-colors">{t('landing.footerSupport')}</a></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4">{t('landing.footerLegal')}</h4>
              <ul className="space-y-2">
                <li><a href="#" className="hover:text-orange-500 transition-colors">{t('landing.footerPrivacyPolicy')}</a></li>
                <li><a href="#" className="hover:text-orange-500 transition-colors">{t('landing.footerTermsOfService')}</a></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-slate-800 mt-8 pt-8 text-center text-slate-400">
            <p>{t('landing.footerCopyright')}</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;

